/**
 * Markdown-flavoured input rules for the lesson-note editor.
 *
 *   # foo               -> heading level 1 (and ## / ### -> 2 / 3)
 *   - foo / * foo       -> bullet list
 *   1. foo              -> ordered list
 *   [ ] foo / [x] foo   -> task list
 *   > foo               -> blockquote
 *   ```                 -> code block
 *   ---                 -> horizontal rule
 *
 * Plus smart typography (curly quotes, em-dash, ellipsis) shipped by
 * prosemirror-inputrules out of the box.
 */
import {
  inputRules,
  wrappingInputRule,
  textblockTypeInputRule,
  smartQuotes,
  ellipsis,
  InputRule,
} from 'prosemirror-inputrules';
import type { NodeType, Schema } from 'prosemirror-model';

function headingRule(nodeType: NodeType, maxLevel: number) {
  return textblockTypeInputRule(
    new RegExp(`^(#{1,${maxLevel}})\\s$`),
    nodeType,
    (match) => ({ level: match[1].length }),
  );
}

function bulletListRule(nodeType: NodeType) {
  return wrappingInputRule(/^\s*([-+*])\s$/, nodeType);
}

function orderedListRule(nodeType: NodeType) {
  return wrappingInputRule(
    /^(\d+)\.\s$/,
    nodeType,
    (match) => ({ order: Number(match[1]) }),
    (match, node) => node.childCount + node.attrs.order === Number(match[1]),
  );
}

function blockquoteRule(nodeType: NodeType) {
  return wrappingInputRule(/^\s*>\s$/, nodeType);
}

function codeBlockRule(nodeType: NodeType) {
  return textblockTypeInputRule(/^```$/, nodeType);
}

function horizontalRuleRule(nodeType: NodeType) {
  return new InputRule(/^(---|___|\*\*\*)\s$/, (state, _match, start, end) => {
    const tr = state.tr.delete(start, end);
    tr.replaceSelectionWith(nodeType.create());
    return tr;
  });
}

/** Task-list input rule: `[ ] ` or `[x] ` at start of a paragraph. */
function taskListRule(schema: Schema) {
  return new InputRule(/^\[( |x|X)\]\s$/, (state, match, start, end) => {
    const taskList = schema.nodes.task_list;
    const taskItem = schema.nodes.task_item;
    if (!taskList || !taskItem) return null;
    const checked = match[1].toLowerCase() === 'x';
    const $start = state.doc.resolve(start);
    // Only trigger from inside a (possibly empty) paragraph that is the only
    // child of its parent block container — the standard wrapping-rule shape.
    const range = $start.blockRange();
    if (!range) return null;
    // Wrap the existing paragraph in task_list > task_item *first*, then
    // delete the literal `[ ] ` / `[x] ` text — positions are mapped through
    // the wrap so the delete targets the right span.
    //
    // (Doing delete-then-wrap was the original implementation and produced
    // "Position out of range" because `range.end` was captured before the
    // delete and pointed past the new doc end.)
    const tr = state.tr;
    tr.wrap(range, [
      { type: taskList, attrs: null },
      { type: taskItem, attrs: { checked } },
    ]);
    // Two extra opening tokens (task_list, task_item) shift everything
    // inside the wrap by +2 positions.
    tr.delete(start + 2, end + 2);
    return tr;
  });
}

/**
 * Smart em-dash rule that DEFERS to the horizontal-rule shortcut.
 *
 * The stock `emDash` from prosemirror-inputrules matches `--$` everywhere,
 * which means the user can never reach `---` for a horizontal rule — after
 * typing the second `-`, `--` gets replaced with `—` immediately. We skip
 * the conversion when `--` sits at the very start of a block so `---<space>`
 * can still fire the horizontal-rule rule.
 */
function smartEmDashRule() {
  return new InputRule(/--$/, (state, _match, start, end) => {
    const $start = state.doc.resolve(start);
    // Skip when `--` is at the very start of a block — reserved for `---` HR.
    if ($start.parentOffset === 0) return null;
    // Skip when the character directly before is also a hyphen — the user is
    // building toward `---`, not finishing an em-dash. This catches the
    // typing sequence `-`, `-`, `-`: when the third `-` arrives, the regex
    // matches the last two dashes (start at position 2 in `---`), but the
    // character before them is also `-`. Without this guard, the third `-`
    // would still get em-dashed to `-—`, killing the HR shortcut.
    const charBefore = state.doc.textBetween(Math.max($start.before() + 1, start - 1), start);
    if (charBefore === '-') return null;
    return state.tr.replaceWith(start, end, state.schema.text('—'));
  });
}

export function buildInputRules(schema: Schema) {
  const rules: InputRule[] = [
    ...smartQuotes,
    ellipsis,
    // Use our start-of-block-aware em-dash rule rather than the stock
    // `emDash` from prosemirror-inputrules, so `---<space>` can still
    // reach `horizontalRuleRule` below.
    smartEmDashRule(),
  ];
  if (schema.nodes.blockquote) rules.push(blockquoteRule(schema.nodes.blockquote));
  if (schema.nodes.bullet_list) rules.push(bulletListRule(schema.nodes.bullet_list));
  if (schema.nodes.ordered_list) rules.push(orderedListRule(schema.nodes.ordered_list));
  if (schema.nodes.code_block) rules.push(codeBlockRule(schema.nodes.code_block));
  if (schema.nodes.heading) rules.push(headingRule(schema.nodes.heading, 3));
  if (schema.nodes.horizontal_rule) rules.push(horizontalRuleRule(schema.nodes.horizontal_rule));
  rules.push(taskListRule(schema));
  return inputRules({ rules });
}
